/*
 * AuditLog.tsx - Änderungen an einem Entity anzeigen.
 *
 * Created by Dr. Maximillian Dornseif 2020
 * Copyright (c) 2020, 2023 HUDORA GmbH
 */

import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky'
import { LadeChecker } from '@hudora/hd-react-components'
import cleanDeep from 'clean-deep'
import sortObject from 'deep-sort-object'
import { cleanGqlInput } from 'graphql-clean-diff'
import gql from 'graphql-tag'
import * as R from 'ramda'
import React from 'react'
import ReactDiffViewer from 'react-diff-viewer'
import { useQuery } from 'urql'

import { IEntity } from '../../types'
import { DisplayUserInfo } from './widgets/DisplayUserInfo'

// AuditLog zu einem Kunden abrufen
export const AUDIT_QUERY = gql`
  query AuditQueryStandAlone($nr: ID!) {
    getAuditLog(forDesignator: $nr) {
      id
      created_at
      forDesignator
      typ
      kind
      uid
      user
      text
      new
      old
    }
  }
`

/** Auditlog anzeigen.
 *
 * Entweder aus einer vom Aufrufer übergebenen Query, oder anhand des Designators selbst abrufen.
 *
 * @deprecated
 */
export const AuditLog = (props: { forDesignator: string; entity?: IEntity }) => {
  if (!props.forDesignator) {
    return <div>Programmierfehler: props.forDesignator fehlt: {JSON.stringify(props)}</div>
  }
  return <AuditLogInner {...props} />
}

function AuditLogInner(props: { forDesignator: any; entity?: IEntity }) {
  const [result] = useQuery({ query: AUDIT_QUERY, variables: { nr: props.forDesignator } })
  const { fetching: loading, error, data } = result
  let details = []
  let audit = data?.getAuditLog
  const sortByCreatedDesc = R.ascend(R.prop('created_at') as any)
  if (audit) {
    // sotieren und normalisieren der Datensätze
    audit = R.sortWith(
      [sortByCreatedDesc],
      audit.map((x: { old: string; new: string }) => {
        return {
          ...x,
          old: cleanDeep(cleanGqlInput(JSON.parse(x.old))),
          new: cleanDeep(cleanGqlInput(JSON.parse(x.new))),
        }
      })
    )
    // sotieren und normalisieren der Daten (old, new)
    audit = audit.map((x: { old: any; new: any }) => {
      return {
        ...x,
        oldText: x.old ? JSON.stringify(sortObject(x.old), null, 2) : '',
        newText: JSON.stringify(sortObject(x.new), null, 2),
      }
    })

    // Dubletten entfernen, Löcher stopfen
    const cleanAudit = []
    if (audit.length > 0) {
      cleanAudit.push(audit.shift())
      let lastLine = cleanAudit[0]
      for (const currentLine of audit) {
        // filtern, wenn es keine Änderung gab
        if (
          currentLine.oldText === lastLine.oldText &&
          currentLine.newText === lastLine.newText &&
          currentLine.oldText === currentLine.newText
        ) {
          continue
        }

        // falls etwas im Hintergrund verändert wurde, neuen Datensatz zufügen
        if (lastLine.newText !== currentLine.oldText) {
          // externe Veränderung
          cleanAudit.unshift({
            ...lastLine,
            created_at: null,
            id: null,
            old: lastLine.new,
            oldText: lastLine.newText,
            new: currentLine.old,
            newText: currentLine.oldText,
            text: `externe Änderung ${lastLine.created_at} ${currentLine.created_at} `,
            typ: 'mutation',
            uid: null,
            user: '?',
          })
        }
        // jetzt die eigentliche Zeile
        cleanAudit.unshift(currentLine)
        lastLine = currentLine
      }
    }

    // Eine Lange "Liste" erstellen.
    details = cleanAudit.map((x: IEntity) => (
      <div key={x.created_at}>
        <Sticky stickyPosition={StickyPositionType.Both}>
          <div
            style={{
              // color: theme.palette.neutralDark,
              padding: '5px 20px 5px 10px',
              fontSize: '130%',
              fontWeight: 'bold',
              borderTop: '1px solid ', // + theme.palette.black,
              borderBottom: '1px solid ', // + theme.palette.black,
            }}
          >
            <div style={{ display: 'inline-block', minWidth: '15em' }}>{x.created_at}</div>
            <div style={{ display: 'inline-block', minWidth: '7em' }}> {`${x.typ}`}</div>
            <div style={{ display: 'inline-block', minWidth: '15em' }}>
              <DisplayUserInfo uid={x.uid} user={x.user} />
            </div>
            <div style={{ display: 'inline-block', minWidth: '15em' }}>{` ${x.text || ''} `}</div>
          </div>
        </Sticky>
        <div>
          <ReactDiffViewer
            oldValue={x.oldText as string}
            newValue={x.newText as string}
            splitView
            hideLineNumbers
            extraLinesSurroundingDiff={0}
            useDarkTheme={false}
            disableWordDiff
          />
        </div>
      </div>
    ))
  }
  return (
    <div data-testid="auditloginner" style={{ maxWidth: '90vw' }}>
      <LadeChecker loading={loading} error={error} label="Lade Protokoll …" />
      {data?.entity?.audit?.length < 1 ? 'keine Protokoll-Daten' : <div>{details}</div>}
      <table className="hd-mytable">
        <tbody>
          {props.entity?.created_at ? (
            <tr>
              <th>Angelegt</th>
              <td>{props.entity?.created_at}</td>
            </tr>
          ) : null}
          {props.entity?.updated_at ? (
            <tr>
              <th>Geändert</th>
              <td>{props.entity?.updated_at}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}
